import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { formatMoney } from "../api";
import { useClientSelection } from "../client-context";
import { formatCustomerId } from "../fulfillment";
import { useApi } from "../hooks";
import {
  ActionForm,
  DataTable,
  RequestView,
  useMutation,
  type InputField,
  type RecordData,
} from "./Workflow";
import Dialog from "./Dialog";

type Provider = "googleads" | "ctm";
type Territory = { location_id: number; market_name: string };
type Request = { loading: boolean; error: string | null; data: unknown; reload: () => void };

/** "a", "a and b", "a, b and c" */
const joinNames = (names: string[]) => (names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);

/**
 * One plain line for an import: what came in, or which account failed and why.
 * The import answers 200 even when an account inside it failed (a rejected CTM
 * key), so the failure is read from its results, not from the request.
 */
function importOutcome(provider: Provider, r: RecordData): { ok: boolean; text: string } {
  const results = (r.results || []) as RecordData[];
  const failed = results.filter((x) => x.status === "failed");
  if (failed.length) {
    const name = (x: RecordData) => provider === "ctm"
      ? String(x.account_name || `CTM account ${x.ctm_account_id}`)
      : String(x.target || `account ${formatCustomerId(String(x.customer_id))}`);
    const reason = String(failed[0].hint || failed[0].error_message || "").trim().slice(0, 300);
    const rest = failed.length < results.length ? " The other accounts imported." : "";
    return { ok: false, text: `Import failed for ${joinNames(failed.map(name))}.${reason ? ` ${reason}` : ""}${rest}` };
  }
  if (provider === "ctm") {
    const added = Number(r.calls_new || 0), updated = Number(r.calls_updated || 0);
    if (!added && !updated) return { ok: true, text: "No new or changed calls in the last 7 days." };
    const parts = [added ? `${added} new` : "", updated ? `${updated} updated` : ""].filter(Boolean);
    return { ok: true, text: `Imported ${parts.join(" and ")} ${added + updated === 1 ? "call" : "calls"}.` };
  }
  if (!Number(r.total_rows || 0)) return { ok: true, text: "No Google Ads spend in the last 7 days." };
  return { ok: true, text: `Imported ${formatMoney(Number(r.total_spend || 0))} of spend from the last 7 days.` };
}

/**
 * The client's Google Ads and CTM accounts, one short table each: which
 * accounts feed this client's spend and calls. Link adds one (optionally for a
 * territory); Unlink removes a wrong one. Importing reads data only.
 */
export default function ProviderMappings() {
  const { clientId, client } = useClientSelection();
  const clientName = client?.name || "this client";
  const ads = useApi<RecordData>("/api/googleads/mapping");
  const ctm = useApi<RecordData>("/api/ctm/mapping");
  const adsClient = (ads.data?.clients as RecordData[] | undefined)?.find((r) => Number(r.client_id) === clientId);
  const territories: Territory[] = adsClient?.territories || [];
  const adsRows: RecordData[] = (adsClient?.accounts || []).filter((r: RecordData) => r.active !== false);
  const ctmRows: RecordData[] = ((ctm.data?.mappings || []) as RecordData[]).filter((r) => Number(r.client_id) === clientId && r.active !== false);
  return (
    <section className="panel workflow-panel provider-mappings">
      <div className="provider-mappings-head">
        <h2>Ad and call accounts</h2>
        <button className="icon-button" aria-label="Refresh linked accounts" title="Refresh linked accounts" onClick={() => { ads.reload(); ctm.reload(); }}>
          <RefreshCw aria-hidden="true" />
        </button>
      </div>
      <MappingGroup provider="googleads" request={ads} rows={adsRows} clientId={clientId} clientName={clientName} territories={territories} />
      <MappingGroup provider="ctm" request={ctm} rows={ctmRows} clientId={clientId} clientName={clientName} territories={territories} />
      <p className="workflow-note">
        Linked accounts decide which client gets imported spend and calls. Importing reads data only; it never changes campaigns.
      </p>
    </section>
  );
}

function MappingGroup({ provider, request, rows, clientId, clientName, territories }: {
  provider: Provider;
  request: Request;
  rows: RecordData[];
  clientId: number | null;
  clientName: string;
  territories: Territory[];
}) {
  const ctm = provider === "ctm";
  const name = ctm ? "CTM" : "Google Ads";
  const mutation = useMutation();
  const [linking, setLinking] = useState(false);
  const [importError, setImportError] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const shown = rows.map((r) => ctm
    ? { id: r.id, account: r.account_name || "CTM account", account_id: String(r.ctm_account_id), territory: r.market_name, calls: Number(r.calls || 0) }
    : { id: r.id, account: r.account_label || "Google Ads account", customer_id: formatCustomerId(String(r.customer_id)), territory: r.market_name });

  async function unlink(row: RecordData) {
    const id = ctm ? row.account_id : row.customer_id;
    if (!window.confirm(`Unlink ${row.account} (${id}) from ${clientName}? New ${ctm ? "calls stop" : "spend stops"} importing for this client. ${ctm ? "Calls" : "Spend"} already imported stays.`)) return;
    setImportError("");
    setBusyId(row.id);
    const done = await mutation.run(ctm ? `/api/ctm/mapping/${row.id}` : `/api/googleads/mapping/account/${row.id}`, {}, "DELETE");
    setBusyId(null);
    if (done) {
      mutation.setNotice(`${row.account} unlinked.`);
      request.reload();
    }
  }

  const territoryField: InputField[] = territories.length
    ? [{ name: "location_id", label: "Territory", options: [{ value: "", label: "Whole client" }, ...territories.map((t) => ({ value: String(t.location_id), label: t.market_name }))] }]
    : [];
  const fields: InputField[] = ctm
    ? [
        { name: "ctm_account_id", label: "CTM account ID", required: true, placeholder: "564504" },
        { name: "account_name", label: "Account name", placeholder: clientName },
        ...territoryField,
      ]
    : [
        { name: "customer_id", label: "Google Ads customer ID", required: true, placeholder: "123-456-7890", hint: "The 10-digit ID at the top of the Google Ads account." },
        { name: "account_label", label: "Account label", placeholder: `${clientName} (Search)` },
        ...territoryField,
      ];

  return (
    <div className="mapping-group">
      <div className="mapping-group-head">
        <h3>{name} accounts</h3>
        <div className="workflow-actions">
          <button className="button secondary" disabled={!clientId} onClick={() => { mutation.setNotice(""); setImportError(""); setLinking(true); }}>
            Link account
          </button>
          <button
            className="button secondary"
            disabled={!clientId || !rows.length || mutation.busy}
            onClick={async () => {
              setImporting(true);
              setImportError("");
              const r = await mutation.run<RecordData>(`/api/${provider}/sync/${clientId}`, { days: 7 });
              setImporting(false);
              if (r) {
                const outcome = importOutcome(provider, r);
                if (outcome.ok) mutation.setNotice(outcome.text);
                else setImportError(outcome.text);
                request.reload();
              }
            }}
          >
            {importing ? "Importing…" : "Import last 7 days"}
          </button>
        </div>
      </div>
      <RequestView request={request}>
        <DataTable
          rows={shown}
          columns={ctm ? ["account", "account_id", "territory", "calls"] : ["account", "customer_id", "territory"]}
          empty={`No ${name} account linked to ${clientName} yet.`}
          action={(row) => (
            <button className="text-button" disabled={mutation.busy} onClick={() => void unlink(row)}>
              {busyId === row.id ? "Unlinking…" : "Unlink"}
            </button>
          )}
        />
      </RequestView>
      {!linking && mutation.feedback}
      {importError && <p className="inline-notice danger" role="alert">{importError}</p>}
      {linking && (
        <Dialog title={`Link ${name} account`} description={`For ${clientName}`} onClose={() => setLinking(false)}>
          <ActionForm
            busy={mutation.busy}
            busyLabel="Linking…"
            submit="Link account"
            fields={fields}
            onSubmit={async (data) => {
              const r = await mutation.run(`/api/${provider}/mapping/${clientId}`, data);
              if (r) {
                setLinking(false);
                request.reload();
                mutation.setNotice("Account linked.");
              }
            }}
          />
          {mutation.feedback}
        </Dialog>
      )}
    </div>
  );
}
